import React, { useState, useEffect } from 'react'
import { useRouteMatch, useHistory, useLocation } from 'react-router-dom'

import {
  useForTablet,
  useForBigScreen,
  SidebarLayout,
  Navbar,
} from '@sketch/components'
import { isCwvRouteOnly, useBrowserSettings } from '@sketch/modules-common'
import {
  useLayoutOverrideProps,
  useCreateLayoutHost,
  useStableHandler,
  createLayoutPortal,
} from '@sketch/utils'
import { HeaderPortalContainer } from '../../components/DocumentSidebarLayout/DocumentSidebarLayout.styles'
import {
  DocumentSidebarLayoutExtraProps,
  LayoutLayoutOverridableProps,
  SidebarLayoutChildrenProps,
} from '../../components/DocumentSidebarLayout/types'

import * as layoutSymbols from '../../components/DocumentSidebarLayout/symbols'

const LayoutComponents = {
  HeaderPortal: createLayoutPortal(layoutSymbols.header),
  SidebarRightPortal: createLayoutPortal(layoutSymbols.sidebarRight),
  FooterPortal: createLayoutPortal(layoutSymbols.footer),
}

interface RenderPropChildren {
  render: (props: DocumentSidebarLayoutExtraProps) => React.ReactElement
}

interface ComponentChildren {
  component: React.ComponentType<DocumentSidebarLayoutExtraProps>
}

type DocumentSidebarLayoutRouteProps = OneOf<
  RenderPropChildren,
  ComponentChildren
>

type DocumentSidebarLayoutProps = DocumentSidebarLayoutRouteProps & {
  title?: string
  darkBackground?: boolean
  sidebarOnTopOfHeader?: boolean
}

interface LocationState {
  toggleSidebarRight?: boolean
}

export const PreviewLayout = ({ ...props }: DocumentSidebarLayoutProps) => {
  const {
    render,
    component: Component,
    title: initialTitle,
    sidebarOnTopOfHeader: initialSidebarOnTopOfHeader = true,
    ...rest
  } = props

  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false)
  const isTabletAndBigger = useForTablet()
  const isDesktopAndBigger = useForBigScreen()

  const location = useLocation<LocationState>()
  const history = useHistory()

  const updateLocationState = useStableHandler(
    (newLocationState: LocationState) => {
      history.replace({
        ...location,
        state: {
          ...location.state,
          ...newLocationState,
        },
      })
    }
  )

  const toggleMobileSidebar = () => {
    // This state flag will help us to know if the user actively
    // toggled the sidebar (we use it only for mobile view)
    updateLocationState({ toggleSidebarRight: true })

    setIsMobileSidebarOpen(isOpen => !isOpen)
  }

  const [data, updateBrowserSettings] = useBrowserSettings()
  const isSidebarRightOpen = data?.sidebarRightOpen

  // For Components Web View pages on tablet we hide the sidebar because we
  // prioritise the left menu sidebar
  const { path } = useRouteMatch()
  const shouldHideRightSidebar =
    isTabletAndBigger && !isDesktopAndBigger && isCwvRouteOnly(path)

  const toggleSidebarRight = () => {
    updateBrowserSettings({
      sidebarRightOpen: !isSidebarRightOpen,
    })
  }

  useEffect(() => {
    if (isSidebarRightOpen && shouldHideRightSidebar) {
      toggleSidebarRight?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isOpen = isTabletAndBigger ? isSidebarRightOpen : isMobileSidebarOpen
  const toggle = isTabletAndBigger ? toggleSidebarRight : toggleMobileSidebar

  // Layout hooks
  const { getOverriddenProps, useOverrideLayoutProps } =
    useLayoutOverrideProps<LayoutLayoutOverridableProps>()
  const { title, darkBackground, sidebarOnTopOfHeader } = getOverriddenProps({
    title: initialTitle,
    hideBackButton: false,
    sidebarOnTopOfHeader: initialSidebarOnTopOfHeader,
  }) as LayoutLayoutOverridableProps

  const headerRef = useCreateLayoutHost(layoutSymbols.header)
  const sidebarRef = useCreateLayoutHost(layoutSymbols.sidebarRight)
  const footerRef = useCreateLayoutHost(layoutSymbols.footer)

  const childrenProps = {
    ...LayoutComponents,
    useOverrideLayoutProps,
  }

  return (
    <SidebarLayout
      darkBackground={darkBackground}
      isSidebarRightOpen={isOpen}
      toggleSidebarRight={toggle}
      title={title}
      sidebarOnTopOfHeader={sidebarOnTopOfHeader}
      header={() => (
        <Navbar data-testid="preview-layout-header" isDocumentView>
          <HeaderPortalContainer ref={headerRef} />
        </Navbar>
      )}
      sidebarRight={<div ref={sidebarRef} />}
      footer={<div ref={footerRef} />}
      {...rest}
    >
      {sidebarLayoutProps => (
        <>
          {render &&
            render({
              ...childrenProps,
              ...(sidebarLayoutProps as SidebarLayoutChildrenProps),
            })}
          {Component && (
            <Component
              {...childrenProps}
              {...(sidebarLayoutProps as SidebarLayoutChildrenProps)}
            />
          )}
        </>
      )}
    </SidebarLayout>
  )
}
